import { useState, useEffect, useCallback } from 'react'

const TOKEN_STORAGE_KEY = 'github_token'
const TOKEN_CACHE_KEY = 'github_token_cache'

interface TokenCache {
  token: string
  timestamp: number
  expiresAt: number
}

const TOKEN_CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 heures

export function useGitHubToken() {
  const [token, setTokenState] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Charger le token depuis le cache au démarrage
  useEffect(() => {
    const loadToken = () => {
      try {
        // Vérifier le cache avec expiration
        const cached = localStorage.getItem(TOKEN_CACHE_KEY)
        if (cached) {
          const cache: TokenCache = JSON.parse(cached)
          const now = Date.now()
          
          if (now < cache.expiresAt) {
            // Cache valide
            setTokenState(cache.token)
            setIsLoading(false)
            return
          } else {
            // Cache expiré, nettoyer
            localStorage.removeItem(TOKEN_CACHE_KEY)
            localStorage.removeItem(TOKEN_STORAGE_KEY)
          }
        }

        // Fallback sur le stockage simple (pour compatibilité)
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY)
        if (stored) {
          setTokenState(stored)
          // Migrer vers le cache
          const cache: TokenCache = {
            token: stored,
            timestamp: Date.now(),
            expiresAt: Date.now() + TOKEN_CACHE_DURATION
          }
          localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache))
        }
      } catch (error) {
        console.error('Error loading GitHub token:', error)
        // Nettoyer en cas d'erreur
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        localStorage.removeItem(TOKEN_CACHE_KEY)
      } finally {
        setIsLoading(false)
      }
    }

    loadToken()
  }, [])

  const setToken = useCallback((newToken: string) => {
    try {
      if (newToken) {
        // Créer le cache avec expiration
        const cache: TokenCache = {
          token: newToken,
          timestamp: Date.now(),
          expiresAt: Date.now() + TOKEN_CACHE_DURATION
        }
        localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache))
        // Garder aussi le stockage simple pour compatibilité
        localStorage.setItem(TOKEN_STORAGE_KEY, newToken)
        setTokenState(newToken)
      } else {
        // Supprimer le token
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        localStorage.removeItem(TOKEN_CACHE_KEY)
        setTokenState(null)
      }
    } catch (error) {
      console.error('Error saving GitHub token:', error)
      throw new Error('Impossible de sauvegarder le token')
    }
  }, [])

  const clearToken = useCallback(() => {
    setToken('')
  }, [setToken])

  const refreshCache = useCallback(() => {
    if (token) {
      const cache: TokenCache = {
        token,
        timestamp: Date.now(),
        expiresAt: Date.now() + TOKEN_CACHE_DURATION
      }
      localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache))
    }
  }, [token])

  return {
    token,
    setToken,
    clearToken,
    refreshCache,
    isLoading,
    hasToken: !!token
  }
}

